import { hydrateRoot } from 'react-dom/client';
import { createOperationDescriptor, getRequest } from 'relay-runtime';
import { RelayEnvironmentProvider } from 'react-relay';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider } from 'react-redux';

import serverVars from 'server-vars';
import { IntlProvider } from 'dibs-react-intl';
import { initializeDBL } from 'dibs-buyer-layout/src/entries/index';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary';
import { Error500Layout } from 'dibs-buyer-layout/exports/error500';
import { DibsWeb3ReactProvider } from 'dibs-crypto-utils/exports/DibsWeb3ReactProvider';
import { getRelayEnvironment } from '../utils/relayModernEnvironment';
import { ServerVarsContextProvider } from '../utils/ServerVarsContext';
import { NftHeaderLayout } from '../nftHeader/NftHeaderLayout';
import { trackPageview } from 'dibs-tracking';

import { paths } from '../nftPublicProfile/constants';
import { NftPublicProfileAddressRespLayout } from '../nftPublicProfile/profileAddress/NftPublicProfileAddressRespLayout';
import { nftPublicProfileAddressEntryQuery } from '../nftPublicProfile/profileAddress/nftPublicProfileAddressEntryQuery';

import type { NftHeaderLayout_viewer$key as NftHeaderLayoutViewer } from '../nftHeader/__generated__/NftHeaderLayout_viewer.graphql';
import type { NftPublicProfileAddressRespLayout_viewer$key as NftPublicProfileAddressViewer } from '../nftPublicProfile/profileAddress/__generated__/NftPublicProfileAddressRespLayout_viewer.graphql';

const locale = serverVars.get('locale');
const environment = getRelayEnvironment({
    ...serverVars.get('dbl.relayData'),
    ...serverVars.get('relay.relayData'),
});

const variables = serverVars.get('relay.variables');

const operation = createOperationDescriptor(
    getRequest(nftPublicProfileAddressEntryQuery),
    variables
);
const snapshot = environment.lookup(operation.fragment);
environment.retain(operation);

initializeDBL().then(({ store }: { store: $TSFixMe }) => {
    hydrateRoot(
        document.getElementById('js-root') as HTMLElement,
        <IntlProvider locale={locale}>
            <ServerVarsContextProvider
                isMobile={serverVars.get('settings.isMobile')}
                isTablet={serverVars.get('settings.isTablet')}
                isDesktop={serverVars.get('settings.isDesktop')}
            >
                <RelayEnvironmentProvider environment={environment}>
                    <ErrorBoundary fallback={({ error }) => <Error500Layout error={error} />}>
                        <Router basename={`${paths.profileAddress}${variables?.walletAddress}`}>
                            <Provider store={store}>
                                <DibsWeb3ReactProvider>
                                    <NftHeaderLayout
                                        viewer={snapshot.data.viewer as NftHeaderLayoutViewer}
                                    >
                                        <NftPublicProfileAddressRespLayout
                                            viewer={
                                                snapshot.data
                                                    .viewer as NftPublicProfileAddressViewer
                                            }
                                            walletAddress={variables?.walletAddress}
                                            ensDomain={serverVars.get('ensDomain')}
                                            userId={variables?.userId}
                                        />
                                    </NftHeaderLayout>
                                </DibsWeb3ReactProvider>
                            </Provider>
                        </Router>
                    </ErrorBoundary>
                </RelayEnvironmentProvider>
            </ServerVarsContextProvider>
        </IntlProvider>
    );
    trackPageview({
        additional: {
            pageType: 'Other',
            pageSubType: 'NFT Public Profile Address',
        },
    });
});
